import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import addressApi from "api/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Select,
  type BaseOption
} from "shared/components";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";
import type { ConstructionType, Unit } from "types/landlord/address";
import { getErrorMessage } from "utils/error";
import { Row } from "views/applications-id-report/components/Row";

const INFER_AUTOMATICALLY = "";

const CONSTRUCTION_TYPE_LABELS: Record<ConstructionType, string> = {
  manufactured_single_section: "Manufactured (Single-Section)",
  manufactured_multi_section: "Manufactured (Multi-Section)",
  modular: "Modular",
  mobile_home_pre_1976: "Mobile home (built prior to 1976)"
};

const CONSTRUCTION_TYPE_OPTIONS: BaseOption[] = [
  { value: INFER_AUTOMATICALLY, label: "Infer automatically" },
  ...Object.entries(CONSTRUCTION_TYPE_LABELS).map(([value, label]) => ({
    value,
    label
  }))
];

const INFERRED_HINT =
  "Not set — inferred automatically from the manufactured date and dimensions";

const EditConstructionTypeForm = ({
  unit,
  onClose
}: {
  unit: Unit;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<ConstructionType | "">(
    unit.construction_type ?? INFER_AUTOMATICALLY
  );

  const { mutate: save, isPending } = useMutation({
    mutationKey: ["UNIT", "CONSTRUCTION_TYPE", "UPDATE"],
    mutationFn: () =>
      addressApi.editUnit(unit.id, { construction_type: value }),
    onSuccess: () => {
      toast.success("Construction type updated");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(
        `Failed to update construction type: ${getErrorMessage(error)}`
      );
    }
  });

  return (
    <SectionCard
      title="Manufactured home"
      footer={
        <SaveCancelButtons
          onSave={() => save()}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    >
      <Select
        id="construction_type"
        label="Construction type"
        hideOptional
        value={value}
        options={CONSTRUCTION_TYPE_OPTIONS}
        onChange={(e) => setValue(e.target.value as ConstructionType | "")}
        legacyErrorMargins={false}
        className="max-w-[330px] w-full"
      />
    </SectionCard>
  );
};

export const ConstructionTypeSection = ({ unit }: { unit: Unit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();

  if (unit.property.kind !== "manufactured_housing") return null;

  if (isEditing)
    return (
      <EditConstructionTypeForm
        unit={unit}
        onClose={() => setIsEditing(false)}
      />
    );

  return (
    <SectionCard
      title="Manufactured home"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <Row
        title="Construction type"
        value={
          unit.construction_type
            ? CONSTRUCTION_TYPE_LABELS[unit.construction_type]
            : INFERRED_HINT
        }
      />
    </SectionCard>
  );
};
